import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { db } from '../../config/firebase';
import { collection, onSnapshot, query, where, addDoc, setDoc, doc} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import ClassNavbar from './ClassNavbar';
import { Assignment } from '@mui/icons-material';

const TEACHER_EMAILS = ['ed@gmail.com', '91xfdc@gmail.com', 'dakshata@educator.synthetica.in'];

const ClassPage = () => {
  const location = useLocation();
  const auth = getAuth();
  const user = auth.currentUser;
  const email = user?.email || '';
  const role = useMemo(() => TEACHER_EMAILS.includes(email) ? 'teacher' : 'student', [email]);
  const classroomId = location.state?.classroomId || '';
  const [subjects, setSubjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadFields, setUploadFields] = useState({ name: '', pid: '', link: '' });
  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    dueDate: '',
    assignedBy: '',
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [assignmentId, setAssignmentId] = useState('');

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'subjects'), (snapshot) => {
      const fetchedSubjects = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setSubjects(fetchedSubjects);
    });

    return () => unsubscribe();
  }, []);

  const fetchAssignments = useCallback((subjectId) => {
    const q = query(
      collection(db, 'assignments'),
      where('subjectId', '==', subjectId),
      where('classroomId', '==', classroomId)
    );

    return onSnapshot(q, (snapshot) => {
      const fetchedAssignments = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setAssignments(fetchedAssignments);
    });
  }, [classroomId]);

  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
    fetchAssignments(subject.id);
  };

  const handleAddAssignment = async (e) => {
    e.preventDefault();
    if (role === 'teacher' && newAssignment.title) {
      try {
        await addDoc(collection(db, 'assignments'), {
          ...newAssignment,
          subjectId: selectedSubject.id,
          classroomId,
        });
        setSnackbarMessage('Assignment added successfully!');
        setSnackbarOpen(true);
        setNewAssignment({ title: '', description: '', dueDate: '', assignedBy: '' });
      } catch (error) {
        console.error('Error adding assignment:', error);
      }
    } else if (role !== 'teacher') {
      alert('You do not have permission to add assignments.');
    } else {
      alert('Please fill in all required fields.');
    }
  };

  const handleUploadSubmission = async (assignmentId) => {
    if (uploadFields.name && uploadFields.pid && uploadFields.link && selectedSubject) {
        try {
            const submissionPath = `submissions/${assignmentId}/${classroomId}`;
            const assignmentDocPath = `submissions/${assignmentId}`; // The path to the assignment document

            // Upload the submission data
            await addDoc(collection(db, submissionPath), {
                ...uploadFields,
                subjectId: selectedSubject.id,
                assignmentId,
                timestamp: new Date().toISOString(),
            });

            // Add a field to the existing document at the assignmentId path
            await setDoc(doc(db, assignmentDocPath), {
                dummyField: 'dummyValue' // Add your dummy field here
            }, { merge: true }); // Use merge: true to add to existing fields without overwriting

            setSnackbarMessage('Submission uploaded successfully!');
            setSnackbarOpen(true);
            closeUploadDialog();
        } catch (error) {
            console.error('Error uploading submission:', error);
        }
    } else {
        alert('Please fill in all fields before uploading.');
    }
  };


  const openUploadDialog = useCallback((assignmentId) => {
    setDialogOpen(true);
    setAssignmentId(assignmentId);
  }, []);

  const closeUploadDialog = useCallback(() => setDialogOpen(false), []);
  const handleSnackbarClose = useCallback(() => setSnackbarOpen(false), []);

  return (
    <Box sx={{ p: 4, bgcolor: '#f9f9f9', minHeight: '100vh' }}>
      <ClassNavbar />

      <Typography variant="h3" color="primary" sx={{ mb: 2 }}>
        Class Page: {location.state?.className || 'Not Assigned'}
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} sm={4}>
          <Card elevation={3}>
            <CardHeader title="Subjects" />
            <CardContent>
              <ul style={{ padding: 0, listStyle: 'none' }}>
                {subjects.map((subject) => (
                  <li key={subject.id} style={{ marginBottom: '1rem' }}>
                    <Typography
                      variant="h6"
                      color="textSecondary"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleSubjectClick(subject)}
                    >
                      {subject.name}
                    </Typography>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={8}>
          {selectedSubject && (
            <Card elevation={3}>
              <CardHeader title={`Assignments for ${selectedSubject.name}`} />
              <CardContent>
                {assignments.length > 0 ? (
                  assignments.map((assignment) => (
                    <Card key={assignment.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="h6">{assignment.title}</Typography>
                        <Typography>
                          <strong>Description:</strong> {assignment.description}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Due:</strong> {assignment.dueDate}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          <strong>Assigned By:</strong> {assignment.assignedBy}
                        </Typography>
                        {role === 'student' && (
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => openUploadDialog(assignment.id)}
                            sx={{ mt: 2 }}
                          >
                            Upload Submission
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Typography>No assignments available for this subject.</Typography>
                )}
              </CardContent>
            </Card>
          )}

          {role === 'teacher' && (
            <form onSubmit={handleAddAssignment} style={{ marginTop: '1rem' }}>
              <Typography variant="h5">Add New Assignment</Typography>
              <TextField
                label="Title"
                value={newAssignment.title}
                onChange={(e) => setNewAssignment({ ...newAssignment, title: e.target.value })}
                fullWidth required margin="normal"
              />
              <TextField
                label="Description"
                value={newAssignment.description}
                onChange={(e) => setNewAssignment({ ...newAssignment, description: e.target.value })}
                fullWidth required margin="normal"
              />
              <TextField
                label="Due Date"
                type="date"
                value={newAssignment.dueDate}
                onChange={(e) => setNewAssignment({ ...newAssignment, dueDate: e.target.value })}
                fullWidth required margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Assigned By"
                value={newAssignment.assignedBy}
                onChange={(e) => setNewAssignment({ ...newAssignment, assignedBy: e.target.value })}
                fullWidth required margin="normal"
              />
              <Button type="submit" variant="contained" color="secondary" sx={{ mt: 2 }}>
                Add Assignment
              </Button>
            </form>
          )}
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={closeUploadDialog}>
        <DialogTitle>Upload Submission</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={uploadFields.name}
            onChange={(e) => setUploadFields({ ...uploadFields, name: e.target.value })}
            fullWidth margin="normal"
          />
          <TextField
            label="PID"
            value={uploadFields.pid}
            onChange={(e) => setUploadFields({ ...uploadFields, pid: e.target.value })}
            fullWidth margin="normal"
          />
          <TextField
            label="Submission Link"
            value={uploadFields.link}
            onChange={(e) => setUploadFields({ ...uploadFields, link: e.target.value })}
            fullWidth margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadDialog} color="primary">Cancel</Button>
          <Button onClick={() => handleUploadSubmission(assignmentId)} color="secondary" variant="contained">
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClassPage;
